'use client';
import { ComputeContextKeys } from '@unifyapps/carbon/no-code/hooks/computeContext/const';
import { APP_SCOPE_ID } from '@unifyapps/carbon/no-code/pulse/scopeRegistry';
import { DEFAULT_DEVICE_VARIANT } from '@unifyapps/defs/types/deviceVariant';
import { fallbackLng } from '@unifyapps/i18n/settings';
import _noop from 'lodash/noop';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { InterfaceModes } from '../../../const';
import RegistryProvider from '../../../context/RegistryProvider';
import { useGetUserContext } from '../../../hooks/useGetUserContext';
import { usePathParams } from '../../../hooks/usePathParams';
import { ActiveThemeProvider } from '../../../theme/ActiveThemeProvider/ActiveThemeProvider';
import { useAppDataSourceRecordStore } from '../../DataSourceRecordStore';
import { AppContextProvider } from '../AppContextProvider';
import { getInitializeLocalStorage } from './actions/getLocalStorageAction';
import { getInitializeSessionStorage } from './actions/getSessionStorageAction';
import { createGlobalAppStateStore } from './createGlobalAppStateStore';
import { createGlobalAppStateStoreSelectors, type GlobalAppStateStoreInstance } from './selectors';
import type { BaseGlobalAppStateStoreProviderProps, GlobalAppStateStore } from './types';
import { getInitialPageVariablesState } from './utils/getInitialPageVariablesState';

const GlobalAppStateStoreContext = createContext<GlobalAppStateStoreInstance | null>(null);

export const useGlobalAppStateStore = () => {
  const _useStore = useContext(GlobalAppStateStoreContext);
  if (!_useStore) {
    throw new Error('Missing GlobalAppStateStoreProvider');
  }
  return _useStore;
};

function getInitialLocation(
  pathParams: GlobalAppStateStore['location']['pathParams'],
): GlobalAppStateStore['location'] {
  if (typeof window === 'undefined' || typeof window.location === 'undefined') {
    return {
      href: '',
      hostname: '',
      pathname: '',
      search: '',
      hash: '',
      pathParams,
    };
  }

  const { pathname, search, hash } = window.location;
  return {
    href: window.location.href,
    hostname: window.location.hostname,
    pathname,
    search,
    hash,
    pathParams,
  };
}

function BaseGlobalAppStateStoreProvider({
  deviceDetails,
  parentInterfaceRecord,
  children,
  permissions,
  remount,
  locale,
  envVariables,
  client,
  mode,
  registry,
  interfaceRecord,
  basePath,
  commonNamespace,
  pageVariables,
  pageFunctions,
  colorSchemeDetails,
  interfaceThemeId,
  appVariantKey,
  pageVariablesInitialValue,
  globalStateStoreRef,
  getDependencyGraphActions,
}: PropsWithChildren<BaseGlobalAppStateStoreProviderProps>) {
  const { pathParams } = usePathParams();
  const getDataSourceRecordsStore = useAppDataSourceRecordStore().getState;
  const { userContext, deviceContext } = useGetUserContext();

  const [store] = useState(() => {
    // keeping all the keys that can be present in the GlobalAppStateStore in the initial value

    const initialValue: GlobalAppStateStore = {
      deviceDetails: deviceDetails ?? {
        device: DEFAULT_DEVICE_VARIANT,
      },
      sessionStorage: getInitializeSessionStorage(interfaceRecord),
      activeAppVariantKey: appVariantKey,
      userContext: (() => {
        const baseUserContext = {
          locale: fallbackLng,
          ...userContext,
          ...(locale ? { locale } : {}),
        };

        return baseUserContext;
      })(),
      deviceContext: deviceContext ?? {},
      appsAndIntegrations: {},
      location: getInitialLocation(pathParams),
      permissions,
      envVariables,
      navigation: {
        isDrawerOpen: false,
        isNotificationPanelOpen: false,
        unopenedNotificationsCount: 0,
      },
      client,
      mode,
      interfaceRecord,
      parentInterfaceRecord,
      pageFunctions: pageFunctions ?? {},
      basePath,
      commonNamespace,
      localStorage: getInitializeLocalStorage(interfaceRecord),
      pageVariables: {},
      blocks: {},
      dataSources: {},
      dataSourcesMeta: {},
      manuallyTriggeredDataSources: {},
      previewTriggeredDataSources: {},
      runTriggeredDataSources: {},
      dataSourceDependencyFlow: {},
      dataSourceIntraDependencies: {},
      manualDataSources: {},
      colorSchemeDetails,
      interfaceThemeId,
    };

    initialValue.pageVariables = pageVariables
      ? getInitialPageVariablesState({
          pageVariables,
          blocks: {},
          // page inputs and page params are for page-level page variables only
          pageInputs: {},
          pageParams: {},
          pageVariablesInitialValue: pageVariablesInitialValue ?? {},
          appState: initialValue,
        })
      : {};

    return createGlobalAppStateStoreSelectors(
      createGlobalAppStateStore({
        initial: initialValue,
        remount,
        getDataSourceRecordsStore,
        scopeId: APP_SCOPE_ID,
        // only the app being edited (builder/preview) feeds Pulse; RUNNER-mode trees rendered
        // alongside it in the builder process (platform manager pages) must never tap.
        capture: mode !== InterfaceModes.RUNNER,
      }),
    );
  });

  useEffect(() => {
    if (globalStateStoreRef) {
      globalStateStoreRef.current = {
        // app-level doesn't use blocks
        updateBlocks: _noop,
        // app-level updatePageVariables not implemented yet
        updatePageVariables: _noop,
        setPageVariables: (variables) => {
          const actions = getDependencyGraphActions?.();
          for (const [id, varState] of Object.entries(variables)) {
            store.getState().actions.setPageVariableState(id, varState);
            actions?.notifyDependencyChange({ id });
            actions?.notifyDependencyChange({ id: ComputeContextKeys.PAGE_VARIABLES });
          }
        },
      };
    }
  }, [store, globalStateStoreRef, getDependencyGraphActions]);

  return (
    <RegistryProvider registry={registry}>
      <GlobalAppStateStoreContext.Provider value={store}>
        <AppContextProvider gas={store}>
          <ActiveThemeProvider store={store}>{children}</ActiveThemeProvider>
        </AppContextProvider>
      </GlobalAppStateStoreContext.Provider>
    </RegistryProvider>
  );
}

export { BaseGlobalAppStateStoreProvider };
